import { Edit } from '@carbon/icons-react';
import { Modal } from '@carbon/react';
import { Button, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AddressForm, {
    AddressFormValues,
    emptyAddressFormValues,
    hasCompleteRequiredAddress,
    normalizeAddressFormValues,
    toAddressDto,
} from 'Components/Common/AddressForm/AddressForm';
import sharedMessages from 'Components/utils/sharedMessages';
import { useUpdateProfileMutation } from 'api/accountEndpoints';
import useGetUser from 'hooks/useGetUser';
import styles from './AddressInput.module.scss';

const addressFields: Array<keyof AddressFormValues> = [
    'street',
    'buildingNumber',
    'apartmentNumber',
    'postalCode',
    'city',
    'country',
];

const areAddressesEqual = (first: AddressFormValues, second: AddressFormValues) => (
    addressFields.every((field) => first[field].trim() === second[field].trim())
);

const AddressValue = ({ label, value }: { label: string; value?: string | null }) => (
    <div>
        <Text size="xs" c="dimmed">
            {label}
        </Text>
        <Text size="sm">
            {value || '-'}
        </Text>
    </div>
);

const AddressInput = () => {
    const intl = useIntl();
    const user = useGetUser();
    const [updateProfile, { isLoading }] = useUpdateProfileMutation();
    const displayedAddress = useMemo(
        () => normalizeAddressFormValues(user?.address),
        [user?.address],
    );
    const [formValues, setFormValues] = useState<AddressFormValues>(emptyAddressFormValues);
    const [initialValues, setInitialValues] = useState<AddressFormValues>(emptyAddressFormValues);
    const [open, setOpen] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const openModal = () => {
        setFormValues(displayedAddress);
        setInitialValues(displayedAddress);
        setSubmitAttempted(false);
        setOpen(true);
    };

    const updateField = (field: keyof AddressFormValues, value: string) => {
        setSubmitAttempted(false);
        setFormValues((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const submit = async () => {
        if (!hasCompleteRequiredAddress(formValues)) {
            setSubmitAttempted(true);
            return;
        }

        await updateProfile({ address: toAddressDto(formValues) }).unwrap();
        setOpen(false);
    };

    const isSaveDisabled = isLoading
        || !hasCompleteRequiredAddress(formValues)
        || areAddressesEqual(formValues, initialValues);

    return (
        <>
            <Paper shadow="sm" p="xl" radius="md" withBorder>
                <GroupHeader
                    title={intl.formatMessage(messages.accountAddress)}
                />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    <AddressValue label={intl.formatMessage(messages.street)} value={displayedAddress.street} />
                    <AddressValue label={intl.formatMessage(messages.buildingNumber)} value={displayedAddress.buildingNumber} />
                    <AddressValue label={intl.formatMessage(messages.apartmentNumber)} value={displayedAddress.apartmentNumber} />
                    <AddressValue label={intl.formatMessage(messages.postalCode)} value={displayedAddress.postalCode} />
                    <AddressValue label={intl.formatMessage(messages.city)} value={displayedAddress.city} />
                    <AddressValue label={intl.formatMessage(messages.country)} value={displayedAddress.country} />
                </SimpleGrid>
                <Button
                    className={styles.editButton}
                    variant="light"
                    leftSection={<Edit size={16} />}
                    onClick={openModal}
                >
                    {intl.formatMessage(messages.editAddress)}
                </Button>
            </Paper>

            <Modal
                open={open}
                onRequestClose={() => { setOpen(false); }}
                onRequestSubmit={submit}
                primaryButtonDisabled={isSaveDisabled}
                loadingStatus={isLoading ? 'active' : undefined}
                modalHeading={intl.formatMessage(messages.accountAddress)}
                primaryButtonText={intl.formatMessage(sharedMessages.save)}
                secondaryButtonText={intl.formatMessage(sharedMessages.cancel)}
                closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
                size="lg"
            >
                <Stack gap="md">
                    <AddressForm
                        values={formValues}
                        onChange={updateField}
                        showValidationErrors={submitAttempted}
                    />
                </Stack>
            </Modal>
        </>
    );
};

const GroupHeader = ({
    title,
}: {
    title: string;
}) => (
    <Stack gap="md" mb="md">
        <Title order={2} size="h3">
            {title}
        </Title>
    </Stack>
);

const messages = defineMessages({
    accountAddress: {
        id: 'src.Screens.Dashboard.Settings.Inputs.AddressInput.accountAddress',
        defaultMessage: 'Dane osoby/konta',
    },
    editAddress: {
        id: 'src.Screens.Dashboard.Settings.Inputs.AddressInput.editAddress',
        defaultMessage: 'Edytuj adres',
    },
    street: {
        id: 'src.Screens.Dashboard.Settings.Inputs.AddressInput.street',
        defaultMessage: 'Ulica',
    },
    buildingNumber: {
        id: 'src.Screens.Dashboard.Settings.Inputs.AddressInput.buildingNumber',
        defaultMessage: 'Numer budynku',
    },
    apartmentNumber: {
        id: 'src.Screens.Dashboard.Settings.Inputs.AddressInput.apartmentNumber',
        defaultMessage: 'Numer lokalu',
    },
    postalCode: {
        id: 'src.Screens.Dashboard.Settings.Inputs.AddressInput.postalCode',
        defaultMessage: 'Kod pocztowy',
    },
    city: {
        id: 'src.Screens.Dashboard.Settings.Inputs.AddressInput.city',
        defaultMessage: 'Miasto',
    },
    country: {
        id: 'src.Screens.Dashboard.Settings.Inputs.AddressInput.country',
        defaultMessage: 'Kraj',
    },
});

export default AddressInput;
